import { useState, useEffect, useCallback, useRef, useMemo } from "react";

/**
 * Custom hook for auto-saving form data to localStorage.
 * Provides debounced saving, error handling, and restoration.
 *
 * @param {string} storageKey - Unique key for localStorage
 * @param {Object} initialData - Initial form data structure
 * @param {number} debounceMs - Debounce delay in milliseconds (default: 500)
 * @returns {Object} - { data, setData, clearSavedData, forceSave, restoreSavedData, hasSavedData, isSaving, lastSaved, saveError }
 */
export const useAutoSave = (storageKey, initialData, debounceMs = 500) => {
  const initialDataRef = useRef(initialData);

  const checkSavedData = useCallback(() => {
    if (typeof window === "undefined" || !window.localStorage) return false;
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && parsed.data && typeof parsed.data === "object") {
          return JSON.stringify(parsed.data) !== JSON.stringify(initialDataRef.current);
        }
      }
    } catch (error) {
      console.error("Error checking saved data:", error);
    }
    return false;
  }, [storageKey]);

  const loadSavedData = useCallback(() => {
    if (typeof window === "undefined" || !window.localStorage) return initialDataRef.current;
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && parsed.data && typeof parsed.data === "object") {
          return parsed.data;
        }
      }
    } catch (error) {
      console.error("Error loading saved data:", error);
    }
    return initialDataRef.current;
  }, [storageKey]);

  const [data, setData] = useState(initialDataRef.current);
  const [hasSavedData, setHasSavedData] = useState(() => checkSavedData());
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [saveError, setSaveError] = useState(null);

  const timeoutRef = useRef(null);
  const dataRef = useRef(data);
  const savingTimeoutRef = useRef(null);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  // Restore lastSaved timestamp on mount
  useEffect(() => {
    if (typeof window === "undefined" || !window.localStorage) return;
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && parsed.timestamp) {
          setLastSaved(new Date(parsed.timestamp));
        }
      }
    } catch (error) {
      console.error("Error checking saved timestamp:", error);
    }
  }, [storageKey]);

  const saveToStorage = useCallback(
    (dataToSave) => {
      try {
        setIsSaving(true);
        setSaveError(null);

        const payload = {
          data: dataToSave,
          timestamp: Date.now(),
          version: "1.0",
        };

        localStorage.setItem(storageKey, JSON.stringify(payload));
        setLastSaved(new Date());

        if (savingTimeoutRef.current) clearTimeout(savingTimeoutRef.current);
        savingTimeoutRef.current = setTimeout(() => {
          setIsSaving(false);
        }, 400);

        console.log(`Auto-saved data for ${storageKey}`);
      } catch (error) {
        setIsSaving(false);
        if (error.name === "QuotaExceededError") {
          setSaveError("Storage quota exceeded. Please clear saved data.");
        } else {
          setSaveError("Failed to save data");
        }
        console.error("Auto-save error:", error);
      }
    },
    [storageKey],
  );

  const debouncedSave = useCallback(
    (dataToSave) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        saveToStorage(dataToSave);
      }, debounceMs);
    },
    [saveToStorage, debounceMs],
  );

  const updateData = useCallback(
    (newData) => {
      const current = dataRef.current;
      const resolvedData = typeof newData === "function" ? newData(current) : newData;
      const hasChanged = JSON.stringify(resolvedData) !== JSON.stringify(current);
      if (hasChanged) {
        setData(resolvedData);
        debouncedSave(resolvedData);
      }
    },
    [debouncedSave],
  );

  const restoreSavedData = useCallback(() => {
    const saved = loadSavedData();
    setData(saved);
    setHasSavedData(true);
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.timestamp) {
          setLastSaved(new Date(parsed.timestamp));
        }
      }
    } catch (error) {
      console.error("Error restoring timestamp:", error);
    }
  }, [loadSavedData, storageKey]);

  const clearSavedData = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
      setLastSaved(null);
      setSaveError(null);
      setData(initialDataRef.current);
      setHasSavedData(false);
      console.log(`Cleared saved data for ${storageKey}`);
    } catch (error) {
      console.error("Error clearing saved data:", error);
    }
  }, [storageKey]);

  const forceSave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    saveToStorage(dataRef.current);
  }, [saveToStorage]);

  // Cleanup pending timeouts on unmount and flush any pending save
  useEffect(() => {
    const initialSnapshot = initialDataRef.current;
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (savingTimeoutRef.current) clearTimeout(savingTimeoutRef.current);

      const current = dataRef.current;
      const isDefault = JSON.stringify(current) === JSON.stringify(initialSnapshot);
      if (!isDefault) {
        saveToStorage(current);
      }
    };
  }, [saveToStorage]);

  return useMemo(
    () => ({
      data,
      setData: updateData,
      clearSavedData,
      forceSave,
      restoreSavedData,
      hasSavedData,
      isSaving,
      lastSaved,
      saveError,
    }),
    [data, updateData, clearSavedData, forceSave, restoreSavedData, hasSavedData, isSaving, lastSaved, saveError],
  );
};

/**
 * Utility function to format last saved time for display
 */
export const formatLastSaved = (date) => {
  if (!date) return "";

  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? "s" : ""} ago`;

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;

  return date.toLocaleDateString();
};
